import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { useWindowResize } from "@/hooks/use-window-resize";

import { chartConfig } from "./config";
import * as c from "./constants";
import { AxisTick } from "./tick";
import { Plot, Props } from "./types";

export const PageViewsChart: FC<Props> = ({
  data = {},
  title = "Page Views",
  description,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<SVGTextElement>(null);
  const windowSize = useWindowResize();
  const [yAxis, setYAxis] = useState(c.INITIAL_AXIS_WIDTH);
  const [done, setDone] = useState(false);

  // SVG doesn't support line breaks
  // The workaround used by D3 is using ticks breaking words
  // However the original state breaks the layout due to Recharts limitations
  const [customWrapper, setCustomWrapper] = useState(false);

  const items = useMemo(
    () =>
      Object.keys(data).map<Plot>((group) => ({
        name: group.replace("|", ", "),
        label: group.split("|")[0],
        views: data[group],
      })),
    [data],
  );

  // Extract the longest label from the input data
  const longestLabel = useMemo(
    () =>
      items.reduce((previous, { label }) => {
        return previous.length > label.length ? previous : label;
      }, ""),
    [items],
  );

  // The real width of the Y axis must be calculated to avoid braking the line.
  // For this reason, another SVG is added to process the width of the longest
  // tick label covers, so it can adjust the actual with of the tick label.
  useEffect(() => {
    if (textRef.current && cardRef.current) {
      const cardWidth = cardRef.current.clientWidth;
      const textWidth = textRef.current.getComputedTextLength();

      if (!cardWidth || !textWidth) {
        return;
      }

      let ticksWidth = textWidth;
      let useCustomWrapper = true;

      if (textWidth > cardWidth / 2) {
        ticksWidth = textWidth / 2;
        useCustomWrapper = false;
      }

      setDone(true);
      setYAxis(ticksWidth + c.DEFAULT_MARGIN);
      setCustomWrapper(useCustomWrapper);
    }
  }, [longestLabel, windowSize]);

  // Calculate the height of all bars with guts
  const chartHeight = useMemo(() => {
    const bars = c.DEFAULT_BAR_SIZE * items.length;
    const guts = c.DEFAULT_MARGIN * items.length - 1;

    return bars + guts;
  }, [items]);

  return (
    <Card className="flex flex-col flex-1" ref={cardRef}>
      <CardHeader className="pb-4">
        <CardTitle className="font-semibold">{title}</CardTitle>
        {description && (
          <CardDescription className="text-sm text-muted-foreground">
            {description}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="flex-1 pb-4 overflow-y-auto">
        {/* Mimic the same SVG for rendering without line breaks */}
        <svg className="absolute invisible">
          <text ref={textRef} className="text-xs">
            {longestLabel}
          </text>
        </svg>
        {done && (
          <ChartContainer
            config={chartConfig}
            className="w-full"
            height={chartHeight + c.DEFAULT_MARGIN * 2}
            ignoreAspectRatio
          >
            <BarChart
              layout="vertical"
              height={chartHeight}
              data={items}
              barSize={c.DEFAULT_BAR_SIZE}
              barGap={c.DEFAULT_MARGIN}
              margin={{
                top: c.DEFAULT_MARGIN,
                right: c.DEFAULT_MARGIN * 2,
                left: c.DEFAULT_MARGIN,
                bottom: c.DEFAULT_MARGIN,
              }}
            >
              <CartesianGrid horizontal={false} />
              <YAxis
                dataKey="label"
                type="category"
                axisLine={false}
                tickLine={false}
                tickMargin={c.DEFAULT_MARGIN}
                width={yAxis}
                tick={customWrapper ? AxisTick : undefined}
              />
              <XAxis dataKey="views" type="number" hide />
              <ChartTooltip
                labelFormatter={(_, [value]) => value.payload.name}
                content={<ChartTooltipContent />}
              />
              <Bar dataKey="views" radius={8} fill="var(--color-blue-600)">
                <LabelList
                  dataKey="views"
                  position="right"
                  fill="var(--primary)"
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};
